import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * The scope a canvas root (`isCanvasRoot`) draws in: the canvas itself, not the node above it.
 * `_render_canvas_item_tree` culls each root from the canvas transform, `Color(1, 1, 1, 1)`,
 * `p_z = 0` and a null material owner (`servers/rendering/renderer_canvas_cull.cpp:70-83`),
 * the four resets `CanvasLayerScope` publishes one canvas further in.
 *
 * Portions ported from Godot Engine (MIT).
 * Copyright (c) 2014-present Godot Engine contributors.
 * Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 * See THIRD-PARTY-NOTICES.md.
 */
import { createContext, useContext, useMemo } from 'react';
import { isCanvasRoot } from './canvasPaintOrder.js';
import { Modulate2DContext, WHITE_MODULATE } from './canvasItemModulate.js';
import { ROOT_TEXTURE_SAMPLER, TextureSampler2DContext } from './canvasItemTextureSampler.js';
import { EffectiveZProvider } from './lighting2d/canvasItemPlacement.js';
import { CanvasItemMaterialProvider } from './components/canvasItemMaterialContext.js';
/**
 * The three-space transform the `<CanvasItem2D>` groups accumulate since the last object whose
 * world matrix is written outright. A `CanvasLayer` mounts no transform in this walk and adds
 * nothing, so a canvas root beneath one still cancels the Node2D above the layer.
 */
const CanvasSpaceContext = createContext(null);
CanvasSpaceContext.displayName = 'CanvasSpaceContext';
/** The accumulated CanvasItem transform in force here, or `null` for none. */
export function useCanvasSpace() {
    return useContext(CanvasSpaceContext);
}
export function CanvasSpaceProvider({ value, children, }) {
    return _jsx(CanvasSpaceContext.Provider, { value: value, children: children });
}
/**
 * Wraps a node's content, resetting the canvas scope when it parents at the
 * canvas. A node that nests normally gets no extra group or context. The node
 * applies it itself: an `instance=` node parses as `Node` until it merges (ADR-0013).
 */
export function CanvasRootScope({ node, parentIsCanvasItem, children, }) {
    const ambient = useCanvasSpace();
    const isRoot = isCanvasRoot(node, parentIsCanvasItem);
    // Visibility still crosses into a top_level child: `_handle_visibility_change` propagates to
    // every CanvasItem child (`canvas_item.cpp:102-108`). So that root stays inside its ancestors'
    // groups, where an eye toggle and `visible = false` reach it, and cancels their transform with
    // this inverse. A root whose parent is no CanvasItem escaped them (`parentSpaceScope.tsx`).
    const inverse = useMemo(() => (isRoot && ambient ? ambient.clone().invert() : null), [isRoot, ambient]);
    if (!isRoot)
        return _jsx(_Fragment, { children: children });
    const scoped = (_jsx(CanvasSpaceProvider, { value: null, children: _jsx(Modulate2DContext.Provider, { value: WHITE_MODULATE, children: _jsx(TextureSampler2DContext.Provider, { value: ROOT_TEXTURE_SAMPLER, children: _jsx(CanvasItemMaterialProvider, { value: null, children: _jsx(EffectiveZProvider, { value: 0, children: children }) }) }) }) }));
    // paint-order-safe: a bare transform group outside the item's own, which
    // carries the canvas key and so still decides `groupOrder` for its pixels.
    return inverse ? (_jsx("group", { matrix: inverse, matrixAutoUpdate: false, children: scoped })) : (scoped);
}
//# sourceMappingURL=canvasRootScope.js.map